/** Every account's driving, and the storage it holds — the cost that compounds.
 *
 * Sessions are private by default, and this is the only screen that lists
 * one account's private sessions to another person. That is why it is behind
 * the admin role, and why it shows less than the owner's own view: no lap
 * table, no racing line, no speed trace. Each row says who else may see the
 * session — nobody, whoever holds its share link, or anybody — and never
 * what the link is: that is the owner's to hand out. What an admin needs to know about a session
 * is whose it is, how big it is and when retention will take it, because the
 * question that brings them here is "where are the bytes going". The card on
 * the right answers that per account, from the same reply, ranked by storage.
 *
 * Purging is the one action, and it is asked twice: the lap documents leave
 * R2 and the rows leave D1 in the same request, the driver is not consulted,
 * and there is no undo. The bucket is asked first and allowed to fail — a
 * refused delete must not leave rows nobody can remove — so the reply says
 * whether it did, and that is repeated here rather than smoothed over. The
 * listing is re-read afterwards, because the storage card was counted on the
 * server and a figure adjusted by hand would be a guess dressed as a count.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { adminSessions, purgeSession } from '@/lib/api';
import { bytes, count, DASH, dateTime, lapTime, plural, relativeTime } from '@/lib/format';
import type { AdminSession, AdminSessionPage, SessionVisibility } from '@/lib/types';
import { visibilityOf } from '@/components/sessions/share';
import {
  Bar,
  Card,
  CardMeta,
  EmptyState,
  ErrorNote,
  Kicker,
  LoadingPanel,
  Page,
  Segmented,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tag,
  TextField,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/profile/ConfirmDialog';
import { AdminHeader, LoadMore, Notice, usePaged, useQueryParams } from '@/components/admin';

type Visibility = 'all' | SessionVisibility;

/** The filter in the address. `?public=true|false` is what links from before
 *  link sharing say, and still opens on the nearest of the three. */
function readVisibility(search: URLSearchParams): Visibility {
  const asked = search.get('visibility');
  if (asked === 'private' || asked === 'link' || asked === 'public') return asked;
  const legacy = search.get('public');
  if (legacy === 'true') return 'public';
  if (legacy === 'false') return 'private';
  return 'all';
}

interface VisibilityTag {
  tone: 'accent' | 'outline' | 'quiet';
  label: string;
  title: string;
}

const VISIBILITY_TAG: Record<SessionVisibility, VisibilityTag> = {
  public: {
    tone: 'accent',
    label: 'public',
    title: 'Listed on the circuit’s compare-with, and readable by anybody',
  },
  link: {
    tone: 'outline',
    label: 'by link',
    title: 'Readable by whoever holds its share link; listed nowhere',
  },
  private: { tone: 'quiet', label: 'private', title: 'Only its owner, and administrators' },
};

const MONO_SMALL = {
  display: 'block',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  color: 'var(--color-neutral-600)',
} as const;

export default function AdminSessions() {
  const [search, setParam] = useQueryParams();
  const userId = search.get('user_id') ?? '';
  const officialId = search.get('official_id') ?? '';
  const visibility = readVisibility(search);
  const narrowed = Boolean(userId || officialId) || visibility !== 'all';

  const [draft, setDraft] = useState({ user_id: userId, official_id: officialId });
  useEffect(() => setDraft({ user_id: userId, official_id: officialId }), [userId, officialId]);

  const fetcher = useCallback(
    (cursor: string | undefined) =>
      adminSessions({
        user_id: userId || undefined,
        official_id: officialId || undefined,
        visibility: visibility === 'all' ? undefined : visibility,
        cursor,
        limit: 50,
      }),
    [userId, officialId, visibility],
  );
  const listing = usePaged<AdminSession, AdminSessionPage>(fetcher);
  const storage = listing.page?.storage_by_user ?? [];
  const largest = storage.reduce((most, row) => Math.max(most, row.bytes), 0);

  const [target, setTarget] = useState<AdminSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<unknown>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const purge = async (session: AdminSession) => {
    setBusy(true);
    setDialogError(null);
    try {
      const done = await purgeSession(session.id);
      if (!alive.current) return;
      setNotice(
        done.bucket_error
          ? {
              tone: 'warn',
              text: `${session.id} purged from the database, but the bucket refused after ${count(done.objects_removed)} of ${plural(done.laps_removed, 'object')}: ${done.bucket_error}. The rest are litter in R2, not rows.`,
            }
          : {
              tone: 'ok',
              text: `${session.id} purged: ${plural(done.laps_removed, 'lap')} and ${plural(done.objects_removed, 'object')} removed`,
            },
      );
      setTarget(null);
      listing.reload();
    } catch (thrown) {
      if (alive.current) setDialogError(thrown);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const submitFilters = (event: FormEvent) => {
    event.preventDefault();
    setParam({ user_id: draft.user_id.trim(), official_id: draft.official_id.trim() });
  };

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader />

      <section aria-label="Sessions" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <Segmented<Visibility>
            label="filter by visibility"
            value={visibility}
            options={[
              { value: 'all', label: 'All' },
              { value: 'private', label: 'Private' },
              { value: 'link', label: 'By link' },
              { value: 'public', label: 'Public' },
            ]}
            onChange={(next) => setParam({ visibility: next === 'all' ? null : next, public: null })}
          />

          <form
            onSubmit={submitFilters}
            style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginLeft: 'auto' }}
          >
            <TextField
              label="account"
              placeholder="usr_…"
              value={draft.user_id}
              onChange={(event) => setDraft({ ...draft, user_id: event.currentTarget.value })}
              style={{ minHeight: 30, fontSize: 12.5, width: 150, fontFamily: 'var(--font-mono)' }}
            />
            <TextField
              label="track"
              placeholder="official_id"
              value={draft.official_id}
              onChange={(event) => setDraft({ ...draft, official_id: event.currentTarget.value })}
              style={{ minHeight: 30, fontSize: 12.5, width: 170, fontFamily: 'var(--font-mono)' }}
            />
            <button type="submit" className="btn btn-secondary" style={{ fontSize: 12 }}>
              Filter
            </button>
            {userId || officialId ? (
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
                onClick={() => setParam({ user_id: null, official_id: null })}
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>

        {notice ? (
          <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
            {notice.text}
          </Notice>
        ) : null}
        {listing.error ? (
          <ErrorNote error={listing.error} title="the sessions could not be read" onRetry={listing.reload} />
        ) : null}

        <div
          className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,340px)]"
          style={{ alignItems: 'start' }}
        >
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {listing.loading ? (
              <LoadingPanel label="loading sessions" />
            ) : listing.items.length === 0 ? (
              <EmptyState title={narrowed ? 'no session matches' : 'no sessions stored'}>
                {narrowed
                  ? 'Clear a filter to widen the search.'
                  : 'A logger with sessions:write sends each lap as it ends; the first session lands here.'}
              </EmptyState>
            ) : (
              <Table scroll label="Sessions" style={{ fontSize: 13 }}>
                <THead>
                  <TR>
                    <TH>Driver</TH>
                    <TH>Track</TH>
                    <TH>Car</TH>
                    <TH>Driven</TH>
                    <TH numeric>Laps</TH>
                    <TH numeric>Best</TH>
                    <TH numeric>Size</TH>
                    <TH>Visibility</TH>
                    <TH>Expires</TH>
                    <TH>
                      <span className="sr-only">actions</span>
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {listing.items.map((session) => (
                    <TR key={session.id}>
                      <TD nowrap>
                        <Link
                          to={`/admin/sessions?user_id=${encodeURIComponent(session.user.id)}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          title="only this account’s sessions"
                        >
                          {session.user.display_name}
                        </Link>
                        <span style={MONO_SMALL}>{session.id}</span>
                      </TD>
                      <TD nowrap title={session.official_id}>
                        <span style={{ display: 'block' }}>{session.track_name || session.official_id}</span>
                        <span style={MONO_SMALL}>{session.source_id}</span>
                      </TD>
                      <TD nowrap muted title={session.car}>
                        <span
                          style={{
                            display: 'block',
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {session.car || DASH}
                        </span>
                      </TD>
                      <TD nowrap muted title={dateTime(session.started_at)}>
                        {relativeTime(session.started_at)}
                        {session.ended_at ? null : (
                          <span style={{ display: 'block', fontSize: 11, color: 'var(--color-warn)' }}>
                            not ended
                          </span>
                        )}
                      </TD>
                      <TD numeric>{count(session.laps)}</TD>
                      <TD numeric color={session.best_lap_ms === null ? undefined : 'var(--color-accent-300)'}>
                        {lapTime(session.best_lap_ms)}
                      </TD>
                      <TD numeric>{session.bytes > 0 ? bytes(session.bytes) : DASH}</TD>
                      <TD nowrap>
                        <Tag
                          tone={VISIBILITY_TAG[visibilityOf(session)].tone}
                          title={VISIBILITY_TAG[visibilityOf(session)].title}
                        >
                          {VISIBILITY_TAG[visibilityOf(session)].label}
                        </Tag>
                      </TD>
                      <TD nowrap muted title={session.expires_at ? dateTime(session.expires_at) : undefined}>
                        {session.expires_at ? relativeTime(session.expires_at) : 'kept'}
                      </TD>
                      <TD nowrap align="right">
                        <button
                          type="button"
                          className="btn btn-danger"
                          style={{ fontSize: 11.5 }}
                          disabled={busy}
                          onClick={() => {
                            setTarget(session);
                            setDialogError(null);
                          }}
                        >
                          Purge…
                        </button>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}

            <LoadMore listing={listing} />
          </div>

          <Card elevation="none" label="Storage by account" as="aside">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <Kicker>storage by account</Kicker>
              <CardMeta className="tabular-nums">sessions and laps · top {count(storage.length)}</CardMeta>
            </div>
            {storage.length === 0 ? (
              <p className="card-body" style={{ color: 'var(--color-neutral-500)' }}>
                Nothing stored yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 2 }}>
                {storage.map((row) => (
                  <div key={row.user_id}>
                    <div
                      className="tabular-nums"
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5 }}
                    >
                      <span
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Link
                          to={`/admin/sessions?user_id=${encodeURIComponent(row.user_id)}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {row.display_name}
                        </Link>
                        <span style={{ color: 'var(--color-neutral-500)' }}>
                          {' '}
                          · {plural(row.sessions, 'session')} · {plural(row.laps, 'lap')}
                        </span>
                      </span>
                      <span style={{ whiteSpace: 'nowrap' }}>{bytes(row.bytes)}</span>
                    </div>
                    <Bar
                      value={row.bytes}
                      max={largest}
                      height={4}
                      label={`${row.display_name}: ${bytes(row.bytes)}`}
                      style={{ marginTop: 5 }}
                    />
                  </div>
                ))}
              </div>
            )}
            <p className="card-meta" style={{ textWrap: 'pretty' }}>
              the nightly sweep applies the retention in Settings; storage is what is left after it
            </p>
          </Card>
        </div>
      </section>

      {target ? (
        <ConfirmDialog
          title="Purge this session?"
          confirmLabel="Purge"
          tone="danger"
          busy={busy}
          error={dialogError}
          onCancel={() => {
            if (busy) return;
            setTarget(null);
            setDialogError(null);
          }}
          onConfirm={() => void purge(target)}
        >
          {target.user.display_name}’s {plural(target.laps, 'lap')} at{' '}
          {target.track_name || target.official_id}, driven {relativeTime(target.started_at)}: the lap
          documents leave R2 and the rows leave the database in one request. The driver is not
          asked, and there is no undo. Retention would take it{' '}
          {target.expires_at ? relativeTime(target.expires_at) : 'never'} on its own.
        </ConfirmDialog>
      ) : null}
    </Page>
  );
}
